// src/utils/taskUtils.js
//
// Task utility functions (Phase 29).
// Frontend mirror of functions/utils/taskUtils.js
//
// After Phase 29, the only function needed from the old scopeTaskId.js
// is isLayoutTask() — it determines whether a task gets a taskState entry.
// Layout tasks (HeaderTask, DividerTask, SpacerTask) have no value in
// taskState and should NOT get taskState entries, but they DO get instanceIds.

export const LAYOUT_COMPONENTS = ['HeaderTask', 'DividerTask', 'SpacerTask']

/**
 * Check if a task is a layout-only component (no value in taskState).
 *
 * @param {string} uiComponent - The task's uiComponent value
 * @returns {boolean}
 */
export function isLayoutTask(uiComponent) {
  return LAYOUT_COMPONENTS.includes(uiComponent)
}

/**
 * Find a taskState entry by its masterTaskId.
 *
 * Config documents and stores that reference master task IDs (workflowTasks doc IDs)
 * need this to look up taskState entries, since taskState keys are instanceIds (UUIDs).
 * Each entry stores { taskId: masterTaskId, ... }.
 *
 * @param {Object} taskState - The customerWorkflows.taskState map
 * @param {string} masterTaskId - The master task ID to find
 * @returns {Object|undefined} The taskState entry, or undefined
 */
export function findTaskStateEntry(taskState, masterTaskId) {
  if (!taskState || !masterTaskId) return undefined
  // Direct key match (backward compat or pre-migration data)
  if (taskState[masterTaskId] !== undefined) return taskState[masterTaskId]
  // Scan entries for matching masterTaskId
  return Object.values(taskState).find(entry => entry?.taskId === masterTaskId)
}

/**
 * Resolve an instanceId to its masterTaskId by scanning workflowStages taskRefs.
 * @param {string} instanceId
 * @param {Array} allStages - From workflowStagesStore.allStages
 * @returns {string|null}
 */
export function resolveInstanceIdToMasterTaskId(instanceId, allStages) {
  if (!instanceId || !allStages) return null
  for (const stage of allStages) {
    const ref = (stage.taskRefs || []).find(r => r.instanceId === instanceId)
    if (ref) return ref.taskId || ref.id || null
  }
  return null
}

/**
 * Find the instanceId for a masterTaskId by scanning workflowStages taskRefs.
 * @param {string} masterTaskId
 * @param {Array} allStages - From workflowStagesStore.allStages
 * @returns {string|null}
 */
export function findInstanceIdFromStages(masterTaskId, allStages) {
  if (!masterTaskId || !allStages) return null
  for (const stage of allStages) {
    const ref = (stage.taskRefs || []).find(r => (r.taskId || r.id) === masterTaskId)
    if (ref?.instanceId) return ref.instanceId
  }
  return null
}

/**
 * Find the instanceId for a masterTaskId scoped to a specific project type.
 * Walks projectType.stageRefs → stage.taskRefs to find the matching task.
 *
 * @param {string} masterTaskId
 * @param {object} projectType - Project type doc with stageRefs
 * @param {Map} stagesById - Map of stageId → stage doc
 * @returns {{ instanceId: string, stageId: string } | null}
 */
export function resolveInstanceIdForProjectType(masterTaskId, projectType, stagesById) {
  if (!masterTaskId || !projectType) return null
  for (const sRef of projectType.stageRefs || []) {
    const stage = stagesById.get(sRef.stageId)
    if (!stage) continue
    for (const tRef of stage.taskRefs || []) {
      if (tRef.taskId === masterTaskId)
        return { instanceId: tRef.instanceId, stageId: sRef.stageId }
    }
  }
  return null
}

/**
 * Substitute `{key}` tokens in a dynamic-task `nameTemplate` with the matching
 * item's values (e.g. "Income Affidavit for {name}" + an adult). Every
 * occurrence of each token is replaced, and the value is inserted LITERALLY:
 * String.replace(stringPattern, value) only replaced the first occurrence and
 * read `$&`, `$'`, `` $` `` and `$$` in a value as replacement patterns
 * (bug NdI0EVVkd2HXw8YsN1Vg). Mirrors substituteDynamicTokens in
 * functions-shared/utils/workflowEngine.js (own keys, in order); an empty
 * template is returned unchanged.
 *
 * @param {string} template
 * @param {object} item - The dynamic source item (adult, member, ...)
 * @returns {string}
 */
export function substituteDynamicTokens(template, item) {
  if (!template || !item) return template
  let out = String(template)
  for (const key of Object.keys(item)) {
    out = out.split(`{${key}}`).join(String(item[key]))
  }
  return out
}
